import {
	EVENT_TYPES,
	type RESPAServiceCode,
	RESPA_SERVICE_NAMES,
} from "@homesite/api-contract";
import { useEventSearch, useRecentEvents } from "@homesite/hooks";
import type {
	EventTimelineItem,
	EventTypeOption,
	RespaCodeOption,
} from "@homesite/ui";
import { EventSearchPage, humanizeEventType } from "@homesite/ui";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { rootRoute } from "./__root";

const EVENT_TYPE_OPTIONS: EventTypeOption[] = EVENT_TYPES.map((et) => ({
	value: et,
	label: humanizeEventType(et),
}));

const RESPA_CODE_OPTIONS: RespaCodeOption[] = (
	Object.entries(RESPA_SERVICE_NAMES) as [RESPAServiceCode, string][]
).map(([code, name]) => ({
	value: code,
	label: `${code.toUpperCase()} — ${name}`,
}));

export const eventSearchRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/events",
	component: function EventSearch() {
		const navigate = useNavigate();
		const {
			searchValue,
			onSearchChange,
			filters,
			onFilterChange,
			results,
			pagination,
			onPageChange,
			loading,
			error,
		} = useEventSearch("event");

		const hasSearched = searchValue.trim().length > 0;
		const { events: recentEvents, loading: recentLoading } =
			useRecentEvents(20);

		// When the user hasn't typed a query, show the recent-events list as the
		// default timeline so the page is never empty.
		const displayedEvents = hasSearched ? results : recentEvents;
		const displayedLoading = hasSearched ? loading : recentLoading;

		const timelineItems: EventTimelineItem[] = displayedEvents.map((e) => ({
			id: e.hash,
			eventType: e.eventType,
			respaCode: e.respaCode,
			respaName: e.respaName,
			hash: e.hash,
			timestamp: e.timestamp,
			verified: e.verified,
			anchored: e.anchored,
		}));

		function handleSelectEvent(hash: string): void {
			void navigate({ to: "/events/$hash", params: { hash } });
		}

		function handleEventTypeChange(v: string | undefined): void {
			onFilterChange("eventType", v ?? "");
		}

		function handleRespaCodeChange(v: string | undefined): void {
			onFilterChange("respaCode", v ?? "");
		}

		return (
			<EventSearchPage
				searchValue={searchValue}
				onSearchChange={onSearchChange}
				results={timelineItems}
				pagination={{
					page: pagination?.page ?? 1,
					pageCount: pagination?.totalPages ?? 1,
				}}
				onPageChange={onPageChange}
				loading={displayedLoading}
				error={error}
				onSelectEvent={handleSelectEvent}
				hasSearched={hasSearched || recentEvents.length > 0}
				eventType={filters.eventType || undefined}
				onEventTypeChange={handleEventTypeChange}
				respaCode={filters.respaCode || undefined}
				onRespaCodeChange={handleRespaCodeChange}
				eventTypeOptions={EVENT_TYPE_OPTIONS}
				respaCodeOptions={RESPA_CODE_OPTIONS}
			/>
		);
	},
});
